"use strict";

/*********************************************************************************************
 * 🛒 SUBSCRIPTION FUNNEL MANAGER
 */
const SubscriptionFunnelManager = {
	config: {
		selectors: {
			root: ".mm-subscription-funnel",
			step: ".mm-sf-step",
			stepTitle: ".mm-sf-step-title",
			stepsLabel: "#sf-steps-label",
			progressFill: "#sf-progress-bar-fill",
			progressBar: "#sf-progress-bar",
			step1Products: "#sf-step1-products",
			step2Products: "#sf-step2-products",
			step3Tabs: "#sf-step3-tabs",
			step3Panels: "#sf-step3-panels",
			actionsBar: "#sf-step-actions",
			nextBtn: "#sf-step-actions .mm-sf-next-btn",
			backBtnDesktop: ".mm-sf-back-btn--desktop:not(.mm-sf-back-btn--step1)",
			backBtnMobile: ".mm-sf-back-btn--mobile:not(.mm-sf-back-btn--step1)",
			sidebarItems: "#sf-sidebar-items",
			sidebarCount: "#sf-sidebar-count",
			sidebarTotal: "#sf-sidebar-total",
			sidebarCompare: "#sf-sidebar-compare",
			sidebarSavings: "#sf-sidebar-savings",
			sidebarToggle: ".mm-sf-sidebar-toggle",
			sidebarOverlay: ".mm-sf-sidebar-overlay",
			toggleCount: "#sf-toggle-count",
			togglePrice: "#sf-toggle-price",
			toggleCompare: "#sf-toggle-compare",
			deliveryEl: "#sf-sidebar-delivery",
			deliveryProgress: "#sf-sidebar-delivery-progress",
			deliveryText: "#sf-sidebar-delivery-text",
			savingsSub: "#sf-sidebar-savings-sub",
			giftDeleteBtn: '[data-action="remove-gift"]',
			announcementBar: ".mm-section-announcement-bar",
			toast: "#sf-incompatible-toast",
			giftToast: "#sf-gift-toast",
		},
	},

	state: {
		currentStep: 1,
		step1: null,
		step2: [],
		step3: [],
		gift: null,
		_giftVisible: false,
		_toastTimer: null,
	},

	init() {
		const root = window.getElement(this.config.selectors.root);
		if (!root) return;

		const giftData = this._parseJSON("sf-gift-data");
		if (giftData) {
			if (giftData.threshold) {
				// Seuil configuré : masquer le cadeau jusqu'à ce que le total l'atteigne
				const giftEl = document.querySelector(".mm-sf-sidebar-gift");
				if (giftEl) {
					giftEl.classList.remove("mm-flex");
					giftEl.classList.add("mm-none");
				}
			} else {
				this.state.gift = giftData;
				this.state._giftVisible = true;
			}
		}

		this._initStep1();
		this._initStep2();
		this._initStep3();
		this._showStep(1);
		this._bindEvents();
		this._updateSidebar();
		requestAnimationFrame(() => requestAnimationFrame(() => this._updateSidebarPeek()));
		window.addEventListener(
			"resize",
			window.debounce(() => this._updateSidebarPeek(), 100),
		);
		window.addEventListener(
			"scroll",
			window.debounce(() => this._updateSidebarPeek(), 50),
			{ passive: true },
		);

		window.log("info", "🛒 [SUBSCRIPTION_FUNNEL]", "Initialized");
	},

	/* ─── Parsing ─────────────────────────────────────────────────────────── */
	_parseJSON(id) {
		const el = document.getElementById(id);
		if (!el) return null;
		try {
			return JSON.parse(el.textContent);
		} catch {
			return null;
		}
	},

	/* ─── Helpers ─────────────────────────────────────────────────────────── */
	_formatMoney(cents) {
		return (cents / 100).toFixed(2).replace(".", ",") + " €";
	},

	/* ─── Step display ────────────────────────────────────────────────────── */
	_showStep(step) {
		document.querySelectorAll(this.config.selectors.step).forEach((el) => {
			el.classList.add("mm-none");
			el.setAttribute("aria-hidden", "true");
		});

		const selector = step === "recap" ? '.mm-sf-step[data-step="recap"]' : `.mm-sf-step[data-step="${step}"]`;
		const target = document.querySelector(selector);
		if (!target) return;
		target.classList.remove("mm-none");
		target.removeAttribute("aria-hidden");
		window.scrollTo({ top: 0, behavior: "smooth" });

		if (typeof step === "number") {
			const label = document.querySelector(this.config.selectors.stepsLabel);
			const root = document.querySelector(this.config.selectors.root);
			const template = root ? root.dataset.stepCounterTemplate : null;
			if (label && template) label.textContent = template.replace("CURRENT", step);

			document.querySelectorAll(this.config.selectors.stepTitle).forEach((t) => t.classList.add("mm-none"));
			const activeTitle = document.getElementById(`sf-step${step}-title`);
			if (activeTitle) activeTitle.classList.remove("mm-none");

			const progressFill = document.querySelector(this.config.selectors.progressFill);
			const progressBar = document.querySelector(this.config.selectors.progressBar);
			if (progressFill && progressBar) {
				const pct = Math.round((step / 3) * 100);
				progressFill.style.width = pct + "%";
				progressBar.setAttribute("aria-valuenow", pct);
			}
		}

		const backBtn = document.querySelector(this.config.selectors.backBtnDesktop);
		const backBtnMobile = document.querySelector(this.config.selectors.backBtnMobile);
		const nextBtn = document.querySelector(this.config.selectors.nextBtn);
		if (!backBtn || !nextBtn) return;

		backBtn.classList.toggle("mm-none", step === 1);
		if (backBtnMobile) backBtnMobile.classList.toggle("mm-none", step === 1);

		document.querySelectorAll(".mm-sf-back-btn--step1").forEach((el) => {
			el.classList.toggle("mm-none", step !== 1);
		});

		const actionsBar = document.querySelector(this.config.selectors.actionsBar);
		const { labelNext, labelCheckout } = actionsBar.dataset;
		const nextSpan = nextBtn.querySelector("span");

		if (step === 3) {
			nextBtn.dataset.action = "checkout";
			if (nextSpan) nextSpan.textContent = labelCheckout;
			nextBtn.setAttribute("aria-label", labelCheckout);
			nextBtn.removeAttribute("disabled");
			nextBtn.removeAttribute("aria-disabled");
		} else {
			nextBtn.dataset.action = "next";
			if (nextSpan) nextSpan.textContent = labelNext;
			nextBtn.setAttribute("aria-label", labelNext);
		}
	},

	_updateCardButton(card, selected) {
		const btn = card.querySelector(".mm-sf-product-add-btn");
		if (!btn) return;
		const span = btn.querySelector("span");
		if (!span) return;
		span.textContent = selected ? btn.dataset.textSelected : btn.dataset.textDefault;
		const check = card.querySelector(".mm-sf-product-check");
		if (check) check.classList.toggle("mm-none", !selected);
	},

	/* ─── Step 1 ──────────────────────────────────────────────────────────── */
	_initStep1() {
		const container = document.querySelector(this.config.selectors.step1Products);
		if (!container) return;
		container.addEventListener("change", (e) => this._onStep1Change(e));
		const firstRadio = container.querySelector(".mm-sf-radio:checked");
		if (firstRadio) firstRadio.dispatchEvent(new Event("change", { bubbles: true }));
	},

	_onStep1Change(e) {
		const radio = e.target;
		if (radio.type !== "radio") return;

		this.state.step1 = {
			variantId: parseInt(radio.value, 10),
			sellingPlanId: parseInt(radio.dataset.sellingPlan, 10),
			price: parseInt(radio.dataset.price, 10),
			compareAtPrice: parseInt(radio.dataset.compareAtPrice, 10) || 0,
			title: radio.dataset.title,
			image: radio.dataset.image,
			imageAlt: radio.dataset.imageAlt,
			shortDescription: radio.dataset.shortDescription || "",
			treatmentDuration: radio.dataset.treatmentDuration || "",
			pillText: radio.dataset.pillText || "",
			pillColor: radio.dataset.pillColor || "",
			pillTextColor: radio.dataset.pillTextColor || "",
			quantity: 1,
			isStep1: true,
		};

		const nextBtn = document.querySelector(this.config.selectors.nextBtn);
		if (nextBtn) {
			nextBtn.removeAttribute("disabled");
			nextBtn.removeAttribute("aria-disabled");
		}

		let recommendedProductId = null;
		document.querySelectorAll("#sf-step1-products .mm-sf-product-card").forEach((card) => {
			const selected = card.dataset.variantId === radio.value;
			card.classList.toggle("mm-sf-product-card--selected", selected);
			this._updateCardButton(card, selected);
			if (selected && card.dataset.recommendedCombo) {
				recommendedProductId = card.dataset.recommendedCombo;
			}
		});

		document.querySelectorAll("#sf-step2-products .mm-sf-product-card").forEach((card) => {
			card.classList.toggle("is-recommended", card.dataset.productId === recommendedProductId);
		});

		this._updateSidebar();
		if (this.state.step2.length > 0) this._removeIncompatibleStep2Selections();
	},

	/* ─── Compatibility ───────────────────────────────────────────────────── */
	_applyCompatibility() {
		const radio = document.querySelector("#sf-step1-products .mm-sf-radio:checked");
		const incompatibleIds =
			radio && radio.dataset.incompatibleWith
				? radio.dataset.incompatibleWith
						.split(",")
						.map((id) => id.trim())
						.filter(Boolean)
				: [];

		document.querySelectorAll("#sf-step2-products .mm-sf-product-card").forEach((card) => {
			const productId = card.dataset.productId;
			const isIncompatible = incompatibleIds.includes(productId);
			card.classList.toggle("is-incompatible", isIncompatible);
			const checkbox = card.querySelector(".mm-sf-checkbox");
			if (checkbox) checkbox.setAttribute("aria-disabled", String(isIncompatible));
			const btn = card.querySelector(".mm-sf-product-add-btn span");
			if (btn) {
				if (isIncompatible) {
					btn.textContent = card.dataset.incompatibleLabel || "";
				} else {
					const input = card.querySelector(".mm-sf-checkbox");
					const addBtn = card.querySelector(".mm-sf-product-add-btn");
					btn.textContent = checkbox && checkbox.checked ? addBtn.dataset.textSelected : addBtn.dataset.textDefault;
				}
			}
		});
	},

	_removeIncompatibleStep2Selections() {
		const radio = document.querySelector("#sf-step1-products .mm-sf-radio:checked");
		const incompatibleIds =
			radio && radio.dataset.incompatibleWith
				? radio.dataset.incompatibleWith
						.split(",")
						.map((id) => id.trim())
						.filter(Boolean)
				: [];

		if (!incompatibleIds.length) return;

		let removed = false;
		document.querySelectorAll("#sf-step2-products .mm-sf-product-card.mm-sf-product-card--selected").forEach((card) => {
			if (incompatibleIds.includes(card.dataset.productId)) {
				const checkbox = card.querySelector(".mm-sf-checkbox");
				if (checkbox && checkbox.checked) {
					checkbox.checked = false;
					checkbox.dispatchEvent(new Event("change", { bubbles: true }));
					removed = true;
				}
			}
		});

		if (removed) {
			const toast = document.querySelector(this.config.selectors.toast);
			if (toast) this._showToast(toast);
		}
	},

	_showToast(toast) {
		if (this.state._toastTimer) clearTimeout(this.state._toastTimer);
		toast.classList.remove("mm-none");
		toast.setAttribute("aria-hidden", "false");
		this.state._toastTimer = setTimeout(() => {
			toast.classList.add("mm-none");
			toast.setAttribute("aria-hidden", "true");
		}, 4000);
	},

	/* ─── Step 2 ──────────────────────────────────────────────────────────── */
	_initStep2() {
		const container = document.querySelector(this.config.selectors.step2Products);
		if (!container) return;
		container.addEventListener("change", (e) => this._onStep2Change(e));
	},

	_preselectStep2() {
		const container = document.querySelector(this.config.selectors.step2Products);
		if (!container) return;
		const firstCompatibleCard = Array.from(container.querySelectorAll(".mm-sf-product-card")).find((card) => !card.classList.contains("is-incompatible"));
		if (!firstCompatibleCard) return;
		const checkbox = firstCompatibleCard.querySelector(".mm-sf-checkbox");
		if (!checkbox || checkbox.checked) return;
		checkbox.checked = true;
		checkbox.dispatchEvent(new Event("change", { bubbles: true }));
	},

	_onStep2Change(e) {
		const checkbox = e.target;
		if (checkbox.type !== "checkbox") return;
		const variantId = parseInt(checkbox.value, 10);

		if (checkbox.checked) {
			this.state.step2.push({
				variantId,
				sellingPlanId: parseInt(checkbox.dataset.sellingPlan, 10),
				price: parseInt(checkbox.dataset.price, 10),
				compareAtPrice: parseInt(checkbox.dataset.compareAtPrice, 10) || 0,
				title: checkbox.dataset.title,
				image: checkbox.dataset.image,
				imageAlt: checkbox.dataset.imageAlt,
				shortDescription: checkbox.dataset.shortDescription || "",
				treatmentDuration: checkbox.dataset.treatmentDuration || "",
				pillText: checkbox.dataset.pillText || "",
				pillColor: checkbox.dataset.pillColor || "",
				pillTextColor: checkbox.dataset.pillTextColor || "",
				quantity: 1,
			});
		} else {
			this.state.step2 = this.state.step2.filter((item) => item.variantId !== variantId);
		}

		const card = checkbox.closest(".mm-sf-product-card");
		if (card) {
			card.classList.toggle("mm-sf-product-card--selected", checkbox.checked);
			this._updateCardButton(card, checkbox.checked);
		}

		this._updateSidebar();
	},

	/* ─── Step 3 ──────────────────────────────────────────────────────────── */
	_initStep3() {
		const tabsContainer = document.querySelector(this.config.selectors.step3Tabs);
		const panelsContainer = document.querySelector(this.config.selectors.step3Panels);
		if (!tabsContainer || !panelsContainer) return;
		tabsContainer.addEventListener("click", (e) => this._onTabClick(e));
		panelsContainer.addEventListener("click", (e) => this._onQtyClick(e));
	},

	_onTabClick(e) {
		const tab = e.target.closest(".mm-sf-tab");
		if (!tab) return;

		document.querySelectorAll(".mm-sf-tab").forEach((t) => {
			t.classList.remove("mm-sf-tab--active");
			t.setAttribute("aria-selected", "false");
		});
		document.querySelectorAll(".mm-sf-tab-panel").forEach((p) => p.classList.add("mm-none"));

		tab.classList.add("mm-sf-tab--active");
		tab.setAttribute("aria-selected", "true");
		const panel = document.getElementById(`sf-panel-${tab.dataset.collectionId}`);
		if (panel) panel.classList.remove("mm-none");
	},

	_addStep3Product(card) {
		const control = card.querySelector(".mm-sf-qty-control");
		const addBtn = card.querySelector(".mm-sf-cross-sell-add-btn");
		addBtn.classList.add("mm-none");
		control.classList.remove("mm-none");
		const variantId = parseInt(card.dataset.variantId, 10);
		this.state.step3.push({
			variantId,
			sellingPlanId: parseInt(card.dataset.sellingPlan, 10),
			price: parseInt(card.dataset.price, 10),
			compareAtPrice: parseInt(card.dataset.compareAtPrice, 10) || 0,
			title: card.dataset.title,
			image: card.dataset.image,
			imageAlt: card.dataset.imageAlt,
			shortDescription: card.dataset.shortDescription || "",
			treatmentDuration: card.dataset.treatmentDuration || "",
			pillText: card.dataset.pillText || "",
			pillColor: card.dataset.pillColor || "",
			pillTextColor: card.dataset.pillTextColor || "",
			quantity: 1,
			isStep3: true,
		});
		card.classList.add("mm-sf-cross-sell-card--selected");
		this._updateSidebar();
	},

	_onQtyClick(e) {
		const card = e.target.closest(".mm-sf-cross-sell-card");
		if (!card) return;

		const btn = e.target.closest(".mm-sf-qty-btn");
		if (btn) {
			const control = btn.closest(".mm-sf-qty-control");
			if (!control) return;
			const variantId = parseInt(card.dataset.variantId, 10);
			const qtyDisplay = control.querySelector(".mm-sf-qty-value");
			let current = parseInt(qtyDisplay.textContent, 10) || 0;

			if (btn.dataset.action === "increase") current++;
			else if (btn.dataset.action === "decrease") current--;

			if (current <= 0) {
				control.classList.add("mm-none");
				card.querySelector(".mm-sf-cross-sell-add-btn").classList.remove("mm-none");
				this.state.step3 = this.state.step3.filter((item) => item.variantId !== variantId);
				card.classList.remove("mm-sf-cross-sell-card--selected");
				this._updateSidebar();
				return;
			}

			qtyDisplay.textContent = current;
			const stateItem = this.state.step3.find((item) => item.variantId === variantId);
			if (stateItem) stateItem.quantity = current;
			this._updateSidebar();
			return;
		}

		if (!card.classList.contains("mm-sf-cross-sell-card--selected")) {
			this._addStep3Product(card);
		}
	},

	/* ─── Sidebar ─────────────────────────────────────────────────────────── */
	_updateSidebar() {
		const itemsEl = document.querySelector(this.config.selectors.sidebarItems);
		const countEl = document.querySelector(this.config.selectors.sidebarCount);
		const totalEl = document.querySelector(this.config.selectors.sidebarTotal);
		const compareEl = document.querySelector(this.config.selectors.sidebarCompare);
		const savingsEl = document.querySelector(this.config.selectors.sidebarSavings);
		if (!itemsEl) return;

		const items = [];
		if (this.state.step1) items.push(this.state.step1);
		this.state.step2.forEach((item) => items.push(item));
		this.state.step3.forEach((item) => items.push(item));

		const scrollTop = itemsEl.scrollTop;
		itemsEl.replaceChildren(...items.map((item) => this._sidebarItemNode(item)));
		itemsEl.scrollTop = scrollTop;

		const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
		this._updateGiftVisibility(total);
		const compareSum = items.reduce((sum, item) => sum + (item.compareAtPrice || item.price) * item.quantity, 0);
		const giftCompare = this.state.gift ? this.state.gift.compare_at_price || 0 : 0;
		const savings = compareSum - total + giftCompare;

		const toggleBtn = document.querySelector(this.config.selectors.sidebarToggle);
		const toggleCountEl = document.querySelector(this.config.selectors.toggleCount);
		const togglePriceEl = document.querySelector(this.config.selectors.togglePrice);
		const toggleCompareEl = document.querySelector(this.config.selectors.toggleCompare);
		if (toggleCountEl && togglePriceEl && toggleBtn) {
			const count = items.reduce((sum, item) => sum + item.quantity, 0);
			const label = count <= 1 ? toggleBtn.dataset.textProduct : toggleBtn.dataset.textProducts;
			toggleCountEl.textContent = count + " " + label + " — ";
			togglePriceEl.textContent = this._formatMoney(total);
			if (toggleCompareEl) {
				if (compareSum > total) {
					toggleCompareEl.textContent = this._formatMoney(compareSum);
					toggleCompareEl.classList.remove("mm-none");
				} else {
					toggleCompareEl.classList.add("mm-none");
				}
			}
		}

		if (countEl) countEl.textContent = items.length;
		if (totalEl) totalEl.textContent = this._formatMoney(total);
		if (compareEl) compareEl.textContent = compareSum > total ? this._formatMoney(compareSum) : "";

		if (savingsEl) {
			const titleEl = savingsEl.querySelector(".mm-sf-sidebar-savings-title");
			if (titleEl) {
				const template = savingsEl.dataset.savingsTemplate;
				if (template) titleEl.textContent = template.replace("{{ montant }}", this._formatMoney(savings)).replace("SAVINGS_AMOUNT", this._formatMoney(savings));
			}
		}

		this._updateDeliveryBar(total);
	},

	_updateDeliveryBar(total) {
		const deliveryEl = document.querySelector(this.config.selectors.deliveryEl);
		if (!deliveryEl) return;
		const threshold = parseInt(deliveryEl.dataset.threshold, 10);
		const thresholdReduced = parseInt(deliveryEl.dataset.thresholdReduced, 10) || 0;
		const progressEl = document.querySelector(this.config.selectors.deliveryProgress);
		const textEl = document.querySelector(this.config.selectors.deliveryText);
		if (!threshold || !progressEl || !textEl) return;

		const reducedActive = thresholdReduced > 0 && thresholdReduced < threshold;

		const pct = Math.min(100, Math.round((total / threshold) * 100));
		progressEl.style.width = pct + "%";

		// Regenere le texte complet depuis le template correspondant a l'etat : le passage
		// d'un etat a l'autre remplace tout le contenu (span #amount inclus), donc on ne peut
		// pas se contenter de mettre a jour un span residuel — il faut reconstruire le HTML.
		if (total >= threshold) {
			textEl.textContent = deliveryEl.dataset.textReached;
		} else if (reducedActive && total >= thresholdReduced) {
			const remaining = this._formatMoney(threshold - total);
			textEl.innerHTML = deliveryEl.dataset.textFree.replace("REMAINING_AMOUNT_PLACEHOLDER", remaining);
		} else {
			const nextThreshold = reducedActive ? thresholdReduced : threshold;
			const remaining = this._formatMoney(nextThreshold - total);
			textEl.innerHTML = deliveryEl.dataset.textRemaining.replace("REMAINING_AMOUNT_PLACEHOLDER", remaining);
		}
	},

	_updateSidebarPeek() {
		const deliveryEl = document.querySelector(this.config.selectors.deliveryEl);
		const toggleEl = document.querySelector(this.config.selectors.sidebarToggle);
		const stepActions = document.querySelector(this.config.selectors.actionsBar);
		const announcementBar = document.querySelector(this.config.selectors.announcementBar);
		const peekHeight = (deliveryEl ? deliveryEl.offsetHeight : 0) + (toggleEl ? toggleEl.offsetHeight : 0);
		const actionsHeight = stepActions ? stepActions.offsetHeight : 88;
		const announcementHeight = announcementBar ? Math.max(0, announcementBar.getBoundingClientRect().bottom) : 0;
		document.documentElement.style.setProperty("--sf-sidebar-peek", peekHeight + "px");
		document.documentElement.style.setProperty("--sf-step-actions-height", actionsHeight + "px");
		document.documentElement.style.setProperty("--sf-announcement-height", announcementHeight + "px");
	},

	_sidebarItemNode(item) {
		const tpl = document.getElementById("sf-sidebar-item-tpl");
		const node = tpl.content.cloneNode(true).firstElementChild;

		node.querySelector(".mm-sf-sidebar-item-image").src = item.image;
		node.querySelector(".mm-sf-sidebar-item-image").alt = item.imageAlt;
		node.querySelector(".mm-sf-sidebar-item-title").textContent = item.title;
		node.querySelector(".mm-sf-sidebar-item-price").textContent = this._formatMoney(item.price * item.quantity);

		const pillEl = node.querySelector(".mm-sf-sidebar-item-pill");
		if (pillEl) {
			if (item.pillText) {
				pillEl.textContent = item.pillText;
				if (item.pillColor) {
					pillEl.style.setProperty("--pill-bg", item.pillColor);
					pillEl.classList.add("mm-bg-custom");
				}
				if (item.pillTextColor) pillEl.classList.add(item.pillTextColor);
			} else {
				pillEl.remove();
			}
		}

		// Prix barre toujours affiche, aligne sur la card : data-compare-at-price contient deja
		// la bonne valeur (compare_at_price Shopify ou prix plein), garantie >= prix abo cote Liquid.
		const compareEl = node.querySelector(".mm-sf-sidebar-item-compare");
		const compareAt = item.compareAtPrice || item.price;
		if (compareAt > item.price) {
			compareEl.textContent = this._formatMoney(compareAt * item.quantity);
			compareEl.classList.remove("mm-none");
		}

		const subtitleEl = node.querySelector(".mm-product-subtitle");
		if (subtitleEl) {
			if (item.shortDescription) {
				subtitleEl.textContent = item.shortDescription;
			} else {
				subtitleEl.remove();
			}
		}

		const qtyEl = node.querySelector(".mm-sf-sidebar-item-qty");
		if (qtyEl) {
			if (item.isStep3) {
				qtyEl.classList.remove("mm-none");
				qtyEl.querySelector(".mm-sf-sidebar-item-qty-value").textContent = item.quantity;
				node.dataset.variantId = item.variantId;
			}
		}

		const durationEl = node.querySelector(".mm-sf-product-treatment-duration");
		const durationSep = node.querySelector(".mm-sf-product-treatment-duration-separator");
		if (durationEl) {
			if (item.treatmentDuration) {
				durationEl.textContent = item.treatmentDuration;
			} else {
				durationEl.remove();
				if (durationSep) durationSep.remove();
			}
		}

		return node;
	},

	/* ─── Checkout ────────────────────────────────────────────────────────── */
	_setCheckoutLoading(loading) {
		const btn = document.querySelector(this.config.selectors.nextBtn);
		if (!btn) return;
		btn.classList.toggle("mm-loading", loading);
	},

	async _checkout() {
		this._setCheckoutLoading(true);

		const toItem = (variantId, quantity, sellingPlanId) => {
			const item = { id: variantId, quantity };
			if (sellingPlanId) item.selling_plan = sellingPlanId;
			return item;
		};

		const items = [];
		if (this.state.step1) items.push(toItem(this.state.step1.variantId, 1, this.state.step1.sellingPlanId));
		this.state.step2.forEach((item) => items.push(toItem(item.variantId, item.quantity, item.sellingPlanId)));
		this.state.step3.forEach((item) => items.push(toItem(item.variantId, item.quantity, item.sellingPlanId)));
		if (this.state.gift) items.push(toItem(this.state.gift.variant_id || this.state.gift.variantId, 1, null));

		try {
			if (window.isTablet() && typeof window.mmReplaceCartAndOpen === "function") {
				await window.mmReplaceCartAndOpen(items);
			} else {
				await fetch("/cart/clear.js", { method: "POST" });
				await fetch("/cart/add.js", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ items }),
				});
				window.location.href = "/checkout";
			}
		} catch (err) {
			this._setCheckoutLoading(false);
			window.log("error", "🛒 [SUBSCRIPTION_FUNNEL]", "Checkout error", { error: err.message });
		}
	},

	/* ─── Gift ────────────────────────────────────────────────────────────── */
	_removeGift(btn) {
		this.state.gift = null;
		this.state._giftVisible = false;
		const giftEl = btn.closest(".mm-sf-sidebar-gift");
		if (giftEl) {
			giftEl.classList.remove("mm-flex");
			giftEl.classList.add("mm-none");
		}
		const subEl = document.querySelector(this.config.selectors.savingsSub);
		if (subEl && subEl.dataset.textWithoutGift) {
			subEl.textContent = subEl.dataset.textWithoutGift;
		}
		this._updateSidebar();
	},

	_updateGiftVisibility(total) {
		const giftData = this._parseJSON("sf-gift-data");
		if (!giftData) return;

		const threshold = giftData.threshold || 0;
		if (!threshold) return;

		const giftEl = document.querySelector(".mm-sf-sidebar-gift");
		if (!giftEl) return;

		const shouldShow = total >= threshold;
		const wasVisible = this.state._giftVisible;

		if (shouldShow && !wasVisible) {
			this.state.gift = giftData;
			this.state._giftVisible = true;
			giftEl.classList.add("mm-flex");
			giftEl.classList.remove("mm-none");
			const subEl = document.querySelector(this.config.selectors.savingsSub);
			if (subEl && subEl.dataset.textWithGift) {
				subEl.textContent = subEl.dataset.textWithGift;
			}
			const toast = document.querySelector(this.config.selectors.giftToast);
			if (toast) this._showToast(toast);
		} else if (!shouldShow && wasVisible) {
			this.state.gift = null;
			this.state._giftVisible = false;
			giftEl.classList.remove("mm-flex");
			giftEl.classList.add("mm-none");
			const subEl = document.querySelector(this.config.selectors.savingsSub);
			if (subEl && subEl.dataset.textWithoutGift) {
				subEl.textContent = subEl.dataset.textWithoutGift;
			}
		}
	},

	/* ─── Navigation ──────────────────────────────────────────────────────── */
	_navigate(direction) {
		const steps = [1, 2, 3];
		const currentIndex = steps.indexOf(this.state.currentStep);

		if (direction === "next") {
			if (this.state.currentStep === 1 && !this.state.step1) return;
			const nextStep = steps[currentIndex + 1];
			if (!nextStep) return;
			this.state.currentStep = nextStep;
			this._showStep(nextStep);
			if (nextStep === 2) {
				this._applyCompatibility();
				this._preselectStep2();
			}
		} else if (direction === "back") {
			const prevStep = steps[currentIndex - 1];
			if (prevStep === undefined) return;
			this.state.currentStep = prevStep;
			this._showStep(prevStep);
		}
	},

	/* ─── Sidebar qty controls ────────────────────────────────────────────── */
	_updateItemQty(btn, action) {
		const itemEl = btn.closest(".mm-sf-sidebar-item");
		if (!itemEl) return;
		const variantId = parseInt(itemEl.dataset.variantId, 10);
		const stateItem = this.state.step2.find((i) => i.variantId === variantId) || this.state.step3.find((i) => i.variantId === variantId);
		if (!stateItem) return;

		const next = action === "qty-increase" ? stateItem.quantity + 1 : stateItem.quantity - 1;

		if (next < 1) {
			this.state.step2 = this.state.step2.filter((i) => i.variantId !== variantId);
			this.state.step3 = this.state.step3.filter((i) => i.variantId !== variantId);

			const step2Checkbox = document.querySelector(`.mm-sf-product-card input[type="checkbox"][value="${variantId}"]`);
			if (step2Checkbox) {
				step2Checkbox.checked = false;
				const step2Card = step2Checkbox.closest(".mm-sf-product-card");
				if (step2Card) {
					step2Card.classList.remove("mm-sf-product-card--selected");
					this._updateCardButton(step2Card, false);
				}
			}

			const step3Card = document.querySelector(`.mm-sf-cross-sell-card[data-variant-id="${variantId}"]`);
			if (step3Card) {
				const qtyControl = step3Card.querySelector(".mm-sf-qty-control");
				const addBtn = step3Card.querySelector(".mm-sf-cross-sell-add-btn");
				if (qtyControl) qtyControl.classList.add("mm-none");
				if (addBtn) addBtn.classList.remove("mm-none");
				step3Card.classList.remove("mm-sf-cross-sell-card--selected");
			}

			this._updateSidebar();
			return;
		}

		stateItem.quantity = next;
		this._updateSidebar();

		const card = document.querySelector(`.mm-sf-cross-sell-card[data-variant-id="${variantId}"]`);
		if (card) {
			const qtyDisplay = card.querySelector(".mm-sf-qty-value");
			if (qtyDisplay) qtyDisplay.textContent = next;
		}
	},

	/* ─── Sidebar mobile toggle ───────────────────────────────────────────── */
	_toggleSidebar() {
		this._updateSidebarPeek();
		const sidebar = document.querySelector(".mm-sf-sidebar");
		const overlay = document.querySelector(this.config.selectors.sidebarOverlay);
		const toggleBtn = document.querySelector('[data-action="toggle-sidebar"]');
		if (!sidebar) return;

		const isOpen = sidebar.classList.contains("mm-sf-sidebar--open");
		sidebar.classList.toggle("mm-sf-sidebar--open", !isOpen);
		if (overlay) overlay.classList.toggle("mm-none", isOpen);
		if (toggleBtn) toggleBtn.setAttribute("aria-expanded", String(!isOpen));

		if (window.isTablet()) {
			if (!isOpen) {
				const scrollY = window.scrollY;
				document.body.style.top = `-${scrollY}px`;
				document.documentElement.classList.add("mm-sf-scroll-locked", "mm-gorgias-hidden");
			} else {
				const scrollY = Math.abs(parseInt(document.body.style.top || "0", 10));
				document.documentElement.classList.remove("mm-sf-scroll-locked", "mm-gorgias-hidden");
				document.body.style.top = "";
				window.scrollTo({ top: scrollY, behavior: "instant" });
			}
		}
	},

	/* ─── Events ──────────────────────────────────────────────────────────── */
	_bindEvents() {
		const root = document.querySelector(this.config.selectors.root);
		root.addEventListener("click", (e) => {
			const btn = e.target.closest("[data-action]");
			if (!btn) return;
			const action = btn.dataset.action;
			if (action === "next" && btn.getAttribute("aria-disabled") !== "true") this._navigate("next");
			if (action === "back") this._navigate("back");
			if (action === "checkout") this._checkout();
			if (action === "toggle-sidebar") this._toggleSidebar();
			if (action === "qty-decrease" || action === "qty-increase") this._updateItemQty(btn, action);
		});

		document.querySelector(this.config.selectors.sidebarOverlay)?.addEventListener("click", () => this._toggleSidebar());

		document.addEventListener("cart:closed", () => this._setCheckoutLoading(false));

		const giftDeleteBtn = document.querySelector(this.config.selectors.giftDeleteBtn);
		if (giftDeleteBtn) {
			giftDeleteBtn.addEventListener("click", () => this._removeGift(giftDeleteBtn));
		}
	},
};

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => SubscriptionFunnelManager.init());
} else {
	SubscriptionFunnelManager.init();
}
